import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, Timestamp, updateDoc, query, where, getDocs, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { toast } from 'react-toastify';
import { useAuth } from '../Auth/Auth';
import logger from '../../utils/logger';
import { formatCurrency } from '../Help/helper';
import BreadCrumb from '../BreadCrumb';
import {
  ClipboardDocumentCheckIcon,
  ShoppingCartIcon,
  BanknotesIcon,
  PhotoIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const CreateGRN = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [formData, setFormData] = useState({
    receivedDate: new Date().toISOString().split('T')[0],
    receivedBy: currentUser?.displayName || currentUser?.email || '',
    deliveryNote: '',
    vehicleNumber: '',
    driverName: '',
    remarks: '',
  });

  const [items, setItems] = useState([]);
  const [photos, setPhotos] = useState([]);

  const pages = [
    { name: 'Procurement', to: '/procurement', component: Link, current: false },
    { name: 'Purchase Orders', to: '/procurement/purchase-orders', component: Link, current: false },
    { name: 'Create GRN', to: '#', component: 'span', current: true },
  ];

  useEffect(() => {
    fetchPurchaseOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'purchaseOrders', poId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const poData = { id: docSnap.id, ...docSnap.data() };
        setPurchaseOrder(poData);

        // Initialize items with quality inspection fields
        const initializedItems = (poData.items || []).map((item, index) => ({
          ...item,
          id: `item-${index}`,
          orderedQuantity: item.quantity,
          receivedQuantity: item.quantity,
          acceptedQuantity: item.quantity,
          rejectedQuantity: 0,
          damageType: '',
          qualityIssue: '',
          inspectionPassed: true,
          remarks: '',
        }));
        setItems(initializedItems);
      } else {
        toast.error('Purchase order not found');
        navigate('/procurement/purchase-orders');
      }
    } catch (error) {
      logger.error('Error fetching purchase order:', error);
      toast.error('Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };

        // Auto-calculate accepted/rejected quantities
        if (field === 'receivedQuantity' || field === 'rejectedQuantity') {
          const received = field === 'receivedQuantity' ? parseInt(value) || 0 : item.receivedQuantity;
          const rejected = field === 'rejectedQuantity' ? parseInt(value) || 0 : item.rejectedQuantity;
          updated.acceptedQuantity = received - rejected;
          updated.inspectionPassed = rejected === 0;
        }

        return updated;
      }
      return item;
    }));
  };

  const handlePhotoUpload = async (e, itemId) => {
    const files = Array.from(e.target.files);
    const newPhotos = [];

    for (const file of files) {
      // Store photo info temporarily
      newPhotos.push({
        file,
        itemId,
        preview: URL.createObjectURL(file),
        name: file.name,
      });
    }

    setPhotos([...photos, ...newPhotos]);
    toast.success(`${files.length} photo(s) added`);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const generateGRNNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(1000 + Math.random() * 9000);
    return `GRN-${year}${month}-${random}`;
  };

  const uploadPhotos = async () => {
    const uploadedPhotos = [];

    for (const photo of photos) {
      try {
        const storageRef = ref(storage, `grn-photos/${Date.now()}-${photo.name}`);
        await uploadBytes(storageRef, photo.file);
        const url = await getDownloadURL(storageRef);
        uploadedPhotos.push({
          url,
          itemId: photo.itemId,
          name: photo.name,
          uploadedAt: Timestamp.now(),
        });
      } catch (error) {
        logger.error('Error uploading photo:', error);
        toast.error(`Failed to upload ${photo.name}`);
      }
    }

    return uploadedPhotos;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.receivedBy) {
      toast.error('Please enter who received the items');
      return;
    }

    const hasDiscrepancy = items.some(item =>
      item.receivedQuantity !== item.orderedQuantity ||
      item.rejectedQuantity > 0
    );

    const hasQualityIssue = items.some(item => !item.inspectionPassed);

    try {
      setCreating(true);

      // Upload photos first
      const uploadedPhotos = await uploadPhotos();

      // Determine status
      let status = 'Fully Received';
      if (hasQualityIssue) {
        status = 'Quality Issue';
      } else if (hasDiscrepancy) {
        const totalOrdered = items.reduce((sum, item) => sum + item.orderedQuantity, 0);
        const totalReceived = items.reduce((sum, item) => sum + item.receivedQuantity, 0);
        status = totalReceived < totalOrdered ? 'Partially Received' : 'Discrepancy';
      }

      // Create GRN
      const grnData = {
        grnNumber: generateGRNNumber(),
        poId: purchaseOrder.id,
        poNumber: purchaseOrder.poNumber,
        procurementId: purchaseOrder.procurementId,
        procurementNumber: purchaseOrder.procurementNumber,
        supplier: purchaseOrder.supplier,
        supplierAddress: purchaseOrder.supplierAddress,
        receivedDate: Timestamp.fromDate(new Date(formData.receivedDate)),
        receivedBy: formData.receivedBy,
        deliveryNote: formData.deliveryNote,
        vehicleNumber: formData.vehicleNumber,
        driverName: formData.driverName,
        remarks: formData.remarks,
        items: items.map(item => ({
          description: item.description,
          orderedQuantity: item.orderedQuantity,
          receivedQuantity: item.receivedQuantity,
          acceptedQuantity: item.acceptedQuantity,
          rejectedQuantity: item.rejectedQuantity,
          unitCost: item.price,
          damageType: item.damageType,
          qualityIssue: item.qualityIssue,
          inspectionPassed: item.inspectionPassed,
          remarks: item.remarks,
        })),
        photos: uploadedPhotos,
        status,
        hasDiscrepancy,
        hasQualityIssue,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.displayName || currentUser?.email || 'System',
      };

      const grnRef = await addDoc(collection(db, 'goodsReceivedNotes'), grnData);

      // Update stock and create movements for accepted items
      for (const item of items) {
        if (item.acceptedQuantity > 0) {
          // Find or create stock item
          const stockQuery = query(
            collection(db, 'stock'),
            where('partNumber', '==', item.description)
          );
          const stockSnapshot = await getDocs(stockQuery);

          let stockItemId = null;
          let previousQuantity = 0;

          if (!stockSnapshot.empty) {
            // Update existing stock
            const stockDoc = stockSnapshot.docs[0];
            stockItemId = stockDoc.id;
            previousQuantity = stockDoc.data().quantity || 0;

            await updateDoc(stockDoc.ref, {
              quantity: increment(item.acceptedQuantity),
              lastUpdated: Timestamp.now(),
            });
          } else {
            // Create new stock item
            const newStockRef = await addDoc(collection(db, 'stock'), {
              itemName: item.description,
              partNumber: item.description,
              quantity: item.acceptedQuantity,
              location: 'RECEIVING BAY',
              unitCost: item.price,
              totalValue: item.price * item.acceptedQuantity,
              type: 'part',
              reorderLevel: 5,
              description: item.description,
              dateAdded: Timestamp.now(),
              lastUpdated: Timestamp.now(),
              status: 'active',
            });
            stockItemId = newStockRef.id;
          }

          // Create movement record
          const newBalance = previousQuantity + item.acceptedQuantity;
          await addDoc(collection(db, 'movements'), {
            stockItemId,
            itemCode: item.description,
            itemDescription: item.description,
            movementType: 'IN',
            transactionType: 'GRN',
            quantityIn: item.acceptedQuantity,
            quantityOut: 0,
            balanceAfter: newBalance,
            unitCost: item.price,
            totalValue: item.price * item.acceptedQuantity,
            date: Timestamp.now(),
            supplier: purchaseOrder.supplier,
            remarks: `GRN #${grnData.grnNumber} - ${hasQualityIssue ? 'With quality issues' : 'Accepted'}`,
            recordedBy: formData.receivedBy,
            location: 'RECEIVING BAY',
            grnId: grnRef.id,
            grnNumber: grnData.grnNumber,
            poId: purchaseOrder.id,
            poNumber: purchaseOrder.poNumber,
          });
        }
      }

      // Update PO status
      const poRef = doc(db, 'purchaseOrders', purchaseOrder.id);
      const totalOrdered = items.reduce((sum, item) => sum + item.orderedQuantity, 0);
      const totalReceived = items.reduce((sum, item) => sum + item.receivedQuantity, 0);

      let newPOStatus = 'Completed';
      if (totalReceived < totalOrdered) {
        newPOStatus = 'Partially Received';
      }

      await updateDoc(poRef, {
        status: newPOStatus,
        statusHistory: [
          ...(purchaseOrder.statusHistory || []),
          {
            status: newPOStatus,
            date: Timestamp.now(),
            updatedBy: formData.receivedBy,
          },
        ],
        lastUpdated: Timestamp.now(),
      });

      toast.success('GRN created successfully!');
      navigate(`/procurement/grn/${grnRef.id}`);
    } catch (error) {
      logger.error('Error creating GRN:', error);
      toast.error('Failed to create GRN');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="text-center py-12">
          <ClipboardDocumentCheckIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <p className="text-white font-medium">Purchase order not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">Create Goods Received Note</h1>
              <p className="text-xs text-gray-600 mt-0.5">
                Record receipt and inspect items for PO #{purchaseOrder.poNumber}
              </p>
            </div>
          </div>
        </div>

        {/* PO Summary */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
            <ShoppingCartIcon className="h-5 w-5 text-white" />
            <h2 className="text-sm font-black text-white">Purchase Order Details</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div>
              <span className="text-xs font-medium text-gray-600">PO #</span>
              <p className="text-xs font-black text-gray-900 mt-0.5">{purchaseOrder.poNumber}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Supplier</span>
              <p className="text-xs font-black text-gray-900 mt-0.5">{purchaseOrder.supplier}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Total</span>
              <p className="text-xs font-black text-green-600 mt-0.5">{formatCurrency(purchaseOrder.grandTotal)}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Left Column - Receipt Form & Items (60%) */}
            <div className="lg:col-span-3 space-y-4">
              {/* Receipt Information */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
                  <TruckIcon className="h-5 w-5 text-white" />
                  <h3 className="text-sm font-black text-white">Receipt Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Received Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.receivedDate}
                      onChange={(e) => setFormData({ ...formData, receivedDate: e.target.value })}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Received By <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.receivedBy}
                      onChange={(e) => setFormData({ ...formData, receivedBy: e.target.value })}
                      required
                      placeholder="Name of person receiving"
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Delivery Note Number
                    </label>
                    <input
                      type="text"
                      value={formData.deliveryNote}
                      onChange={(e) => setFormData({ ...formData, deliveryNote: e.target.value })}
                      placeholder="Supplier's delivery note #"
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Vehicle Number
                    </label>
                    <input
                      type="text"
                      value={formData.vehicleNumber}
                      onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                      placeholder="Delivery vehicle registration"
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Driver Name
                    </label>
                    <input
                      type="text"
                      value={formData.driverName}
                      onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                      placeholder="Delivery driver name"
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      General Remarks
                    </label>
                    <input
                      type="text"
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                      placeholder="Any general observations"
                      className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* Items Inspection */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
                  <h3 className="text-sm font-black text-white">Quality Inspection</h3>
                </div>
                <div className="space-y-4 mt-4 max-h-[600px] overflow-y-auto">
                  {items.map((item, index) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-xs text-gray-900">{item.description}</h4>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Ordered: {item.orderedQuantity} | Unit Price: {formatCurrency(item.price)}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-black rounded-full ${
                            item.inspectionPassed ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {item.inspectionPassed ? '✓ Pass' : '✗ Fail'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Received Qty <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            value={item.receivedQuantity}
                            onChange={(e) => handleItemChange(item.id, 'receivedQuantity', parseInt(e.target.value) || 0)}
                            min="0"
                            required
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Rejected Qty
                          </label>
                          <input
                            type="number"
                            value={item.rejectedQuantity}
                            onChange={(e) => handleItemChange(item.id, 'rejectedQuantity', parseInt(e.target.value) || 0)}
                            min="0"
                            max={item.receivedQuantity}
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Accepted Qty
                          </label>
                          <input
                            type="number"
                            value={item.acceptedQuantity}
                            readOnly
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 bg-gray-50 shadow-sm font-bold text-emerald-600"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Damage Type
                          </label>
                          <select
                            value={item.damageType}
                            onChange={(e) => handleItemChange(item.id, 'damageType', e.target.value)}
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">None</option>
                            <option value="Physical Damage">Physical Damage</option>
                            <option value="Wrong Item">Wrong Item</option>
                            <option value="Expired">Expired</option>
                            <option value="Poor Quality">Poor Quality</option>
                            <option value="Incomplete">Incomplete</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Quality Issue Details
                          </label>
                          <textarea
                            rows={2}
                            value={item.qualityIssue}
                            onChange={(e) => handleItemChange(item.id, 'qualityIssue', e.target.value)}
                            placeholder="Describe any quality issues..."
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Item Remarks
                          </label>
                          <textarea
                            rows={2}
                            value={item.remarks}
                            onChange={(e) => handleItemChange(item.id, 'remarks', e.target.value)}
                            placeholder="Any additional notes..."
                            className="block w-full px-2 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>

                      {/* Photo Upload */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          <PhotoIcon className="h-4 w-4 inline mr-1" />
                          Upload Photos (for damaged/rejected items)
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handlePhotoUpload(e, item.id)}
                          className="block w-full text-xs text-gray-500
                            file:mr-2 file:py-1.5 file:px-3
                            file:rounded-xl file:border-0
                            file:text-xs file:font-medium
                            file:bg-green-50 file:text-green-700
                            hover:file:bg-green-100"
                        />
                        {/* Photo Previews */}
                        {photos.filter(p => p.itemId === item.id).length > 0 && (
                          <div className="mt-2 grid grid-cols-4 gap-2">
                            {photos.filter(p => p.itemId === item.id).map((photo, photoIndex) => {
                              const globalIndex = photos.findIndex(p => p === photo);
                              return (
                                <div key={photoIndex} className="relative">
                                  <img
                                    src={photo.preview}
                                    alt={`Preview ${photoIndex}`}
                                    className="w-full h-16 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(globalIndex)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Summary (40%) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Summary Cards */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
                  <BanknotesIcon className="h-5 w-5 text-white" />
                  <h3 className="text-sm font-black text-white">Receipt Summary</h3>
                </div>
                <div className="space-y-3 mt-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-gray-600">Total Ordered</p>
                    <p className="text-2xl font-black text-gray-900 mt-1">
                      {items.reduce((sum, item) => sum + item.orderedQuantity, 0)}
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-emerald-700">Total Accepted</p>
                    <p className="text-2xl font-black text-emerald-900 mt-1">
                      {items.reduce((sum, item) => sum + item.acceptedQuantity, 0)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-red-700">Total Rejected</p>
                    <p className="text-2xl font-black text-red-900 mt-1">
                      {items.reduce((sum, item) => sum + item.rejectedQuantity, 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <Link
              to={`/procurement/purchase-orders/${poId}`}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
            >
              {creating ? 'Creating GRN...' : 'Complete Receipt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGRN;
